import { StaffApiService, SupervisorApiService } from '../core/api/services';
import { AlertService } from '../shared/services/alert.service';
import { Utils } from '../core/utils';
import {
  PatientPlaylistModel,
  PatientProgramModel,
  PatientTargetModel,
  PatientBehaviorModel,
  PromptScheduleModel,
  SkillAcquisitionObjectiveModel,
  SkillAreaModel,
  TreatmentGoalModel,
  BehaviorGoalModel,
} from '../core/api/models';
import { SupervisorHelperService } from './supervisor.helper.service';
import { catchError, forkJoin, Observable, of } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { AlertHtmlComponent } from '../shared/alerts/alert-html/alert-html.component';
import { PatientDataFacadeService } from '../core/services/patient-data-facade.service';

export class SupervisorBaseComponent {
  isLoading: boolean = false;

  constructor(
    protected supervisorApiService: SupervisorApiService,
    protected alertService: AlertService,
    protected supervisorHelperService: SupervisorHelperService,
    protected authService: AuthService,
    protected staffApiService: StaffApiService,
    protected patientDataFacade: PatientDataFacadeService
  ) { }
  
  loadBaseData(patientID: number, finishedLoading?: () => void) {
    console.log('loadBaseData called');

    this.isLoading = true;

    const getPlaylistsObservable =
      this.supervisorApiService.GetPatientPlaylists({ patientID: patientID });

    const getSkillAreasObservable = this.supervisorApiService.GetSkillAreas({
      patientID: patientID,
    });

    const getProgramsObservable = this.patientDataFacade.getPrograms(patientID, {
      onlyActive: false,
    });

    const getTargetsObservable = this.patientDataFacade.getTargets(patientID, {
      onlyActive: false,
    });

    const getPromptSchedulesObservable =
      this.supervisorApiService.GetPromptSchedules({
        patientID: patientID,
      });

    const getTreatmentGoalsObservable =
      this.supervisorApiService.GetPatientTreatmentGoals({
        patientID: patientID,
      });

    const getSkillAcquisitionGoalsObservable = this.supervisorApiService.GetPatientSkillAcquisitionObjectives({
      patientID: patientID,
    });

    const getBehaviorGoalsObservable = this.supervisorApiService.GetPatientBehaviorGoals({
      patientID: patientID,
    });

    const getBehaviorsObservable = this.staffApiService.GetPatientBehaviors({
      patientID: patientID,
    });

    const getDataObservables: Observable<
      | PatientPlaylistModel[]
      | SkillAreaModel[]
      | PatientProgramModel[]
      | PatientTargetModel[]
      | PromptScheduleModel[]
      | TreatmentGoalModel[]
      | SkillAcquisitionObjectiveModel[]
      | BehaviorGoalModel[]
      | PatientBehaviorModel[]
    >[] = [
        getPlaylistsObservable,
        getSkillAreasObservable,
        getProgramsObservable,
        getTargetsObservable,
        getPromptSchedulesObservable,
        getTreatmentGoalsObservable,
        getSkillAcquisitionGoalsObservable,
        getBehaviorGoalsObservable,
        getBehaviorsObservable,
      ];

    // Wrap each API call with catchError to handle failure individually
    const wrappedCalls = getDataObservables.map((call) =>
      call.pipe(catchError((error) => of({ error: true, details: error })))
    );

    forkJoin(wrappedCalls).subscribe(
      ([
        playlists,
        skillAreas,
        programs,
        targets,
        promptSchedules,
        treatmentGoals,
        skillAcquisitionGoals,
        behaviorGoals,
        behaviors,
      ]) => {

        
        if ('error' in programs) {
          console.log('Error fetching programs:', programs.details);
        } else {
          this.supervisorHelperService.programs.set(
            programs as PatientProgramModel[]
          );
        }
        if ('error' in playlists) {
          console.log('Error fetching playlists:', playlists.details);
        } else {
          this.supervisorHelperService.playlists.set(
            playlists as PatientPlaylistModel[]
          );
        }
        if ('error' in skillAreas) {
          console.log('Error fetching skill areas:', skillAreas.details);
        } else {
          this.supervisorHelperService.skillAreas.set(
            skillAreas as SkillAreaModel[]
          );
        }
        if ('error' in skillAcquisitionGoals) {
          console.log('Error fetching skill acquisition goals:', skillAcquisitionGoals.details);
        } else {
          this.supervisorHelperService.skillAcquisitionGoals.set(
            skillAcquisitionGoals as SkillAcquisitionObjectiveModel[]
          );
        }
        if ('error' in behaviorGoals) {
          console.log('Error fetching patient behavior goals:', behaviorGoals.details);
        } else {
          this.supervisorHelperService.behaviorGoals.set(
            behaviorGoals as BehaviorGoalModel[]
          );
        }
        if ('error' in targets) {
          console.log('Error fetching targets:', targets.details);
        } else {
          this.supervisorHelperService.targets.set(
            targets as PatientTargetModel[]
          );
        }
        if ('error' in promptSchedules) {
          console.log(
            'Error fetching prompt schedules:',
            promptSchedules.details
          );
        } else {
          this.supervisorHelperService.promptSchedules.set(
            promptSchedules as PromptScheduleModel[]
          );
        }
        if (treatmentGoals && 'error' in treatmentGoals) {
          console.log(
            'Error fetching treatment goals:',
            treatmentGoals.details
          );
        } else {
          this.supervisorHelperService.treatmentGoals.set(
            treatmentGoals as TreatmentGoalModel[]
          );
        }
        if ('error' in behaviors) {
          console.log('Error fetching behaviors:', behaviors.details);
        } else {
          this.supervisorHelperService.behaviors.set(
            behaviors as PatientBehaviorModel[]
          );
        }
        this.isLoading = false;
        if (finishedLoading) {
          finishedLoading();
        }
      }
    );
  }

  playlistsToDisplay(programID: number): string {
    let playlists = this.supervisorHelperService.playlists();
    playlists.forEach((playlist) => {
      playlist.Items?.sort((a, b) => {
        if (a.OrderNr && b.OrderNr) {
          return a.OrderNr - b.OrderNr;
        }
        return 0;
      });
    });
    let programsPlaylists = playlists
      .filter((playlist) =>
        playlist.Items?.find((item) => item.PatientProgramID === programID)
      );


    return programsPlaylists.length === 0
      ? 'none'
      : programsPlaylists.map((playlist) => playlist.PlaylistName).join(', ');
  }

  skillAreaToDisplay(skillAreaID: number): string {
    let programSkillArea = this.supervisorHelperService
      .skillAreas()
      .find((skillArea) => skillArea.ID === skillAreaID);
    if (programSkillArea) {
      return programSkillArea.SkillAreaName ?? 'Skill Area Name Not Found';
    } else {
      return 'none';
    }
  }
}
